import { useState } from 'react'
import CLUB from '../../config/club.js'
import { PBCC_PREMIUM, HUSTLER } from '../../config/siteMode.js'

// Shared UI kit for every Pro Shop Suite module — keeps modules thin and
// consistent (mirrors the Shop Credit / Special Orders look). Self-contained
// (no imports back into module pages) to avoid circular deps.
export const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
export const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
export const CREAM = CLUB.creamColor || '#FFFFFF'
// Section titles / rules sit DIRECTLY on the dark panel (not in a cream card),
// so on premium they must be gold + light — dark-green text would be invisible.
export const TITLE_ON_DARK = HUSTLER ? 'var(--ge-primary)' : (PBCC_PREMIUM ? 'var(--ge-gold)' : PRIMARY)
export const SUB_ON_DARK = HUSTLER ? '#4b5563' : (PBCC_PREMIUM ? '#D8CFB8' : '#6B7280')

export const money = (n) => (n == null || n === '' ? '—' : `$${(Number(n) || 0).toFixed(2)}`)
export const todayISO = () => new Date().toISOString().slice(0, 10)
export const fmtDate = (iso) => {
  const d = iso ? new Date(`${iso}T00:00`) : null
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : (iso || '—')
}
export const daysUntil = (iso) => {
  if (!iso) return null
  const d = new Date(`${iso}T00:00`), t = new Date(todayISO() + 'T00:00')
  return Math.round((d - t) / 86400000)
}

export function Card({ children, className = '', style = {} }) {
  return (
    <div className={`rounded-2xl p-4 ${className}`} style={{ background: CREAM, border: `1px solid ${ACCENT}44`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)', ...style }}>
      {children}
    </div>
  )
}

export function SectionRule({ label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px w-6" style={{ background: ACCENT }} />
      <span className="text-xs font-extrabold uppercase tracking-widest" style={{ color: TITLE_ON_DARK }}>{label}</span>
      <span className="h-px flex-1" style={{ background: `${ACCENT}55` }} />
    </div>
  )
}

export function Title({ title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div>
        <span className="block h-0.5 w-8 mb-1" style={{ background: ACCENT }} />
        <h2 className="text-lg sm:text-xl" style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: TITLE_ON_DARK }}>{title}</h2>
        {subtitle ? <p className="text-xs mt-0.5" style={{ color: SUB_ON_DARK }}>{subtitle}</p> : null}
      </div>
      {right}
    </div>
  )
}

export function Err({ msg }) {
  return msg ? <p className="text-xs font-bold mt-1" style={{ color: '#B91C1C' }}>{msg}</p> : null
}

export function Pill({ children, bg = 'rgba(0,0,0,0.06)', fg = '#4B5563' }) {
  return <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: bg, color: fg }}>{children}</span>
}

// Stage badge tint helper — cycles a green/gold family so pipelines read well.
const STAGE_TINTS = [
  { bg: 'rgba(107,114,128,0.12)', fg: '#4B5563' },
  { bg: 'rgba(37,99,235,0.12)', fg: '#1D4ED8' },
  { bg: 'rgba(99,102,241,0.14)', fg: '#4338CA' },
  { bg: 'rgba(34,168,90,0.14)', fg: '#15803D' },
  { bg: 'rgba(var(--ge-primary-rgb),0.20)', fg: 'var(--ge-primary)' },
  { bg: 'rgba(var(--ge-primary-rgb),0.14)', fg: 'var(--ge-primary)' },
]
export function stageTint(stages, status) {
  const i = Math.max(0, stages.indexOf(status))
  return STAGE_TINTS[Math.min(i, STAGE_TINTS.length - 1)]
}

// Forward/back stage stepper used by pipeline modules (repairs, orders…).
export function StagePipeline({ stages, status, onMove, disabled }) {
  const idx = stages.indexOf(status)
  return (
    <div className="flex items-center gap-2">
      <button type="button" className="btn-secondary text-xs" disabled={disabled || idx <= 0} onClick={() => onMove(-1)} title="Back a stage">◀</button>
      <div className="flex-1 text-center text-xs font-bold" style={{ color: PRIMARY }}>{status}</div>
      <button type="button" className="btn-secondary text-xs" disabled={disabled || idx >= stages.length - 1} onClick={() => onMove(1)} title="Forward a stage">▶</button>
    </div>
  )
}

export function Field({ label, children }) {
  return (
    <label className="block">
      {label ? <span className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-0.5">{label}</span> : null}
      {children}
    </label>
  )
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(var(--ge-primary-rgb),0.78)' }} onClick={onClose}>
      <div className={`w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} rounded-2xl my-8`} style={{ background: CREAM, border: `1px solid ${ACCENT}44`, boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${ACCENT}33` }}>
          <h3 className="text-lg font-extrabold" style={{ color: PRIMARY, fontFamily: 'Cinzel, serif' }}>{title}</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none" style={{ color: PRIMARY }} aria-label="Close">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

// Copyable member-notification block (mirrors Special Orders). The provider
// hookup is deliberately a stub — wire SMS/email here later.
export function NotifyBox({ message, channel = 'text/email' }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(message); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch {}
  }
  return (
    <div>
      <div className="rounded-xl p-3 text-sm" style={{ background: '#fff', border: `1px solid ${ACCENT}44`, color: PRIMARY }}>{message}</div>
      <div className="flex items-center gap-2 mt-2">
        <button type="button" className="btn-primary text-sm" onClick={copy}>{copied ? 'Copied ✓' : '📋 Copy message'}</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2 leading-snug">
        {/* WIRE PROVIDER HERE LATER: automatic {channel} send is a future add-on — for now copy & send manually. */}
        Automatic {channel} send is a future add-on (provider hookup pending). Copy and send it for now.
      </p>
    </div>
  )
}

// Standard text/number inputs pre-styled to the suite.
export const inputCls = 'input'
